/**
 * Shared, data-agnostic helpers for report dashboards (voice + SMS campaign
 * reports). Color is semantic and token-only (see styles/tokens.css) so light
 * and dark both resolve through CSS variables.
 *
 * These were originally inline in `pages/voice/campaign-report.tsx`; they were
 * lifted here verbatim so the SMS campaign report can share the exact same
 * visual language with zero drift.
 */

// Semantic palette — token-only so light/dark both resolve. `muted` maps to
// the low-emphasis text token for neutral/terminal buckets.
export type Sem = 'success' | 'warn' | 'danger' | 'info' | 'muted';

export const semVar: Record<Sem, string> = {
  success: 'var(--success)',
  warn: 'var(--warn)',
  danger: 'var(--danger)',
  info: 'var(--info)',
  muted: 'var(--text-4)',
};

/**
 * A single labeled, colored quantity — the shape shared by stacked-bar
 * buckets, donut segments, and legend rows.
 */
export interface ReportBucket {
  key: string;
  label: string;
  count: number;
  sem: Sem;
}

// chipBg returns the semantic soft-fill token for a KPI chip icon backdrop.
export function chipBg(sem: Sem): string {
  switch (sem) {
    case 'success':
      return 'var(--success-bg)';
    case 'warn':
      return 'var(--warn-bg)';
    case 'danger':
      return 'var(--danger-bg)';
    case 'info':
      return 'var(--info-bg)';
    default:
      return 'var(--surface-2)';
  }
}

export function fmtPct(n: number): string {
  if (n === 0) return '0%';
  if (n >= 1) return `${n.toFixed(1)}%`;
  return `${Number(n.toFixed(2))}%`;
}

export function pctInt(n: number): string {
  return `${Math.round(n)}%`;
}

export function formatRate(v: number | undefined | null): string {
  if (v === undefined || v === null) return '—';
  return `${(v * 100).toFixed(1)}%`;
}
